import { IMGS_PATH } from 'constants/image';
import clsx from 'clsx';
import { mappingByTranslation } from 'components/ui/Icon/Icons/Countries/mapping';
import { useState } from 'react';
import { Direction } from 'types/Utilities';
import RichText from 'components/ui/ContentBlocks/RichText';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import tailwind from 'utils/breakpoints';
import { useTranslation } from 'hooks/useTranslation';
import LocationMap from './LocationMap';
import type { ClinicPage } from 'types/Clinic';

type Props = {
  clinic: ClinicPage;
};

const About = ({ clinic }: Props): JSX.Element => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isTextExtended, setIsTextExtended] = useState(false);
  const [isLocationExtended, setIsLocationExtended] = useState(true);

  const accreditationMapping: Record<string, string> = {
    'AAAASF Accredited Surgery Facility': 'AAAASF',
    'Hospital-accreditation-thailand': 'Hospital-accreditation-thailand',
    ISHRS: 'ISHRS',
    'ISO 9001': 'ISO-9001',
    'ISO 10002': 'iso-10002_n',
    'ISO 14001': 'iso-14001_n',
    ISQua: 'ISQua',
    JCI: 'JCI-joint-commission-international',
    'Temos-international-dental-care': 'Temos-international-dental-care',
    TEMOS: 'TEMOS',
    GMC: 'GMC',
    'Care Quality Commission': 'care_quality_commission',
  };

  const getFlagIconCountryCode = (language: string): string[] => {
    return Object.keys(mappingByTranslation).filter((key) => {
      const array =
        mappingByTranslation[key as keyof typeof mappingByTranslation];
      return array.includes(language);
    });
  };

  return (
    <div id="about" className={clsx('pb-12 lg:pt-0')}>
      <div className={clsx('max-w-screen-xl mx-auto px-5 md:px-4')}>
        <div className="lg:grid grid-cols-3">
          <div className="flex justify-between items-center lg:items-start">
            <h2 className="adelle-bold-section-headline">
              {t('clinicPage.aboutTheClinic')}
            </h2>
            <Icon
              type="Arrow"
              direction={isCollapsed ? Direction.BOTTOM : Direction.TOP}
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="lg:hidden cursor-pointer h-4 w-4 mb-4 text-navy stroke-current mt-12"
              color={tailwind.theme.backgroundColor.navy}
            />
          </div>
          <div
            className={clsx(
              isCollapsed && 'hidden',
              'lg:block col-span-2 text-charcoal',
            )}
          >
            <div
              className={clsx(
                'leading-8 lg:leading-6 text-with-links',
                !isTextExtended &&
                  clinic.about &&
                  'h-40 overflow-hidden lg:overflow-visible lg:h-auto',
              )}
            >
              {clinic.about && <RichText fields={clinic.about} />}
              {!clinic.about && !!clinic.locationDescription && (
                <RichText fields={clinic.locationDescription} />
              )}
            </div>
            {clinic.about && (
              <div className="lg:hidden">
                {!isTextExtended && '...'}
                <button
                  className="text-navy font-bold cursor-pointer underline"
                  onClick={() => setIsTextExtended(!isTextExtended)}
                >
                  {isTextExtended ? t('showLess') : t('showMore')}
                </button>
              </div>
            )}
            <div className="lg:flex">
              {clinic.accreditation && (
                <div className="lg:order-2 pt-1 lg:pt-0 lg:w-4/12 shrink-0 flex-wrap">
                  <p className="font-bold my-6 mb-2">
                    {t('clinicPage.accreditations')}
                  </p>
                  <div className="flex flex-wrap">
                    {clinic.accreditation.map((acc, idx) => (
                      <div className="w-20 mr-8" key={idx}>
                        <Image
                          src={`${IMGS_PATH.PUBLIC}/accreditations/${accreditationMapping[acc]}.jpg`}
                          alt={acc}
                          width={80}
                          height={80}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {clinic.translatedLanguages && (
                <div className="lg:mr-16 lg:w-6/12 shrink-0 flex-wrap">
                  <p className="font-bold my-6">{t('languages')}</p>
                  <div className="flex flex-wrap">
                    {clinic.translatedLanguages.map(
                      (language: string, i: number) => {
                        let [countryCode] = getFlagIconCountryCode(language);
                        if (countryCode === 'EN') {
                          countryCode = 'GB';
                        }

                        return (
                          <div
                            key={i}
                            className="text-center w-28 lg:w-24 mb-6"
                          >
                            {countryCode && (
                              <div className="w-10 mx-auto border border-bluishGreyDarker rounded-sm overflow-hidden">
                                <Icon type={`Countries.${countryCode}`} />
                              </div>
                            )}
                            <span className="text-sm">
                              {t(`language.${language}`)}
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </div>
            {clinic.location && clinic.locationDescription && (
              <>
                <div className="flex justify-between items-center mb-4 lg:mb-6 mt-14 lg:mt-10">
                  <p className="font-bold">{t('location')}</p>
                  <Icon
                    type="Arrow"
                    direction={
                      isLocationExtended ? Direction.TOP : Direction.BOTTOM
                    }
                    onClick={() => {
                      setIsLocationExtended(!isLocationExtended);
                    }}
                    className="lg:hidden cursor-pointer h-4 w-4 stroke-current"
                  />
                </div>
                <div
                  className={clsx(
                    isLocationExtended ? 'lg:block' : 'hidden',
                    'lg:grid',
                    clinic.locationDescription ? 'grid-cols-3' : 'grid-cols-1',
                  )}
                >
                  <div
                    className={clsx(
                      'leading-8 lg:leading-6 text-with-links',
                      !clinic.location && 'col-span-3',
                    )}
                  >
                    {clinic.locationDescription && (
                      <RichText fields={clinic.locationDescription} />
                    )}
                  </div>
                  {clinic.location &&
                  clinic.location.lat &&
                  clinic.location.lon ? (
                    <div
                      className={clsx(
                        'overflow-hidden rounded-lg w-full lg:col-span-2 mb-4 mt-4 lg:mt-0',
                        clinic.locationDescription && 'lg:ml-6',
                      )}
                    >
                      <LocationMap
                        latitude={clinic.location.lat}
                        longitude={clinic.location.lon}
                        locationName={clinic.name}
                        addressLine={clinic.address}
                        cityName={clinic.city.name}
                        countryName={clinic.city.country.name}
                      />
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
